import {
  Component,
  computed,
  DestroyRef, effect,
  ElementRef,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { heroTrash } from '@ng-icons/heroicons/outline';
import { heroCog8ToothSolid, heroMicrophoneSolid, heroPauseSolid, heroPlaySolid } from '@ng-icons/heroicons/solid';
import { IconComponent } from 'app/shared/icon/icon.component';
import { ValueAccessorDirective } from '../value-accessor.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import WaveSurfer from 'wavesurfer.js';

export type StateType = 'idle' | 'recording' | 'recorded' | 'play' | 'pause' | 'resume' | 'denied';

@Component({
  selector: 'app-voice-recording',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './voice-recording.component.html',
  styleUrls: ['./voice-recording.component.scss'],
  providers: [provideIcons({ heroMicrophoneSolid, heroPlaySolid, heroPauseSolid, heroTrash, heroCog8ToothSolid })],
  hostDirectives: [ValueAccessorDirective],
  host: {
    class: 'block',
  },
})
export class VoiceRecordingComponent implements OnDestroy, OnInit, OnChanges {
  @Input() audioUrl: string | undefined;
  isEditMode = input(false);
  isPreviewMode = input(false);

  audioUrlSignal = signal<string | undefined>(undefined);
  transcriptChange = output<string>();
  removeAudio = output<boolean>();
  recorded = output<boolean>();
  stateChanged = output<StateType>();

  private destroyRef = inject(DestroyRef);
  valueAccessor = inject(ValueAccessorDirective);
  state = signal<StateType>('idle');
  recordingTime = 0; // Timer in seconds
  timerInterval: any = null; // Timer reference
  isUploaded = signal(false);

  waveSurfer: WaveSurfer | null = null;
  waveformContainer!: ElementRef;

  @ViewChild('waveform', { static: false }) set content(content: ElementRef) {
    if (content) {
      this.waveformContainer = content;
      this.initWaveform();
    }
  }

  isRecorded = computed(
    () =>
      this.state() === 'recorded' || this.state() === 'play' || this.state() === 'pause' || this.state() === 'resume',
  );
  isIdle = computed(() => this.state() === 'idle');
  isRecording = computed(() => this.state() === 'recording');
  isPlay = computed(() => this.state() === 'play');
  isPause = computed(() => this.state() === 'pause');
  isResume = computed(() => this.state() === 'resume');
  isDenied = computed(() => this.state() === 'denied');
  audioDuration = 0; // Total audio duration in seconds
  audioCurrentTime = 0; // Current playback time
  maxAudioDuration = 300000; // 5 minutes in milliseconds

  speechRecognition;
  mediaRecorder: MediaRecorder | null = null;
  audio: HTMLAudioElement | null = null;
  audioChunks: Blob[] = [];

  constructor() {
    // Check if browser supports SpeechRecognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.speechRecognition = new SpeechRecognition();
    } else {
      console.error('SpeechRecognition is not supported in this browser.');
    }
  }

  ngOnInit(): void {
    this.audioUrlSignal.set(this.audioUrl as string);

    if (this.isPreviewMode()) {
      this.setState('recorded');
      // this.state.set('recorded');

      if (this.audioUrlSignal()) {
        this.audio = new Audio(this.audioUrlSignal());
        this.isUploaded.set(true);
      }
    }

    this.valueAccessor.value.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
      this.audioUrlSignal.set(value);
      this.initWaveform();

      this.audio = new Audio(this.audioUrlSignal());
      if (value) {
        this.setState('recorded');
        // this.state.set('recorded');
      } else {
        this.setState('idle');
        // this.state.set('idle');
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['audioUrl']) {
      this.audioUrlSignal.set(changes['audioUrl'].currentValue);
    }
  }

  setState(state: StateType) {
    this.state.set(state)
    this.stateChanged.emit(this.state())
  }

  ngOnDestroy(): void {
    this.setState('idle');
    // this.state.set('idle');
    this.audioDuration = 0;
    this.audioCurrentTime = 0;
    this.audioUrlSignal.set(undefined);
    this.isUploaded.set(false);
    clearInterval(this.timerInterval); // Stop the timer
    if (this.waveSurfer) {
      this.waveSurfer = null;
    }
  }

  onRemove() {
    this.setState('idle');
    // this.state.set('idle');
    this.valueAccessor.valueChange(null);
    clearInterval(this.timerInterval); // Stop the timer
    this.audioDuration = 0;
    this.audioCurrentTime = 0;

    this.audioUrlSignal.set(undefined);
    this.waveSurfer?.destroy();
    this.removeAudio.emit(this.isUploaded());
    this.isUploaded.set(false);
  }

  onIconClick() {
    switch (this.state()) {
      case 'idle':
        this.startRecording();
        break;
      case 'recording':
        this.stopRecording();
        break;
      case 'recorded':
        this.playAudio();
        break;
      case 'play':
        this.pauseAudio();
        break;
      case 'pause':
        this.resumeAudio();
        break;
    }
  }

  initWaveform() {
    if (!this.audioUrlSignal() || !this.waveformContainer) return;

    this.waveSurfer = WaveSurfer.create({
      container: this.waveformContainer.nativeElement,
      waveColor: '#3ABFF8',
      progressColor: '#0284C7',
      cursorColor: 'transparent',
      barWidth: 3,
      barHeight: 1,
      height: 40,
      width: '94%',
      normalize: true,
      autoCenter: true,
      hideScrollbar: true,
    });

    this.waveSurfer.load(this.audioUrlSignal() as string);
  }

  startRecording(): void {
    // Start recording audio
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      this.setState('recording');
      // this.state.set('recording');
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/mp4; codecs="avc1.4d002a"'
      });
      this.audioChunks = []; // Reset audio chunks

      this.mediaRecorder.ondataavailable = (event) => {
        this.audioChunks.push(event.data); // Store audio data chunks
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.audioUrlSignal.set(URL.createObjectURL(audioBlob)); // Create URL for playback

        this.audio = new Audio(this.audioUrlSignal());

        this.initWaveform();
        this.valueAccessor.valueChange(this.audioUrlSignal());
        this.valueAccessor.touchedChange(true);
        this.recorded.emit(true);
      };

      this.mediaRecorder.start();
    });

    // Start SpeechRecognition
    if (this.speechRecognition) {
      this.speechRecognition.lang = 'en-US'; // Set language
      this.speechRecognition.interimResults = false; // Disable partial results
      this.speechRecognition.continuous = true; // Stop after the user finishes speaking

      this.speechRecognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript; // Extract the text
        this.transcriptChange.emit(transcript);
      };

      this.speechRecognition.onerror = (event: any) => {
        if (event.error === 'not-allowed') {
          this.setState('denied');
          // this.state.set('denied');
        }
        clearInterval(this.timerInterval);
        console.error('Error occurred:', event.error);
      };

      this.speechRecognition.start();
    }

    // Start Timer
    this.recordingTime = 0;
    this.timerInterval = setInterval(() => {
      this.recordingTime++;
    }, 1000);
  }

  stopRecording(): void {
    // Stop audio recording
    if (this.mediaRecorder) {
      this.setState('recorded');
      // this.state.set('recorded');
      this.mediaRecorder.stop();
    }

    // Stop SpeechRecognition
    if (this.speechRecognition) {
      this.speechRecognition.stop();
    }

    clearInterval(this.timerInterval); // Stop the timer
  }

  playAudio(): void {
    if (this.waveSurfer) {
      this.waveSurfer.play();
      this.setState('play');
      // this.state.set('play');
    }

    if (this.audioUrlSignal() && this.audio) {
      this.setState('play');
      // this.state.set('play');
      this.audio.play();

      this.audio.ontimeupdate = () => {
        this.audioCurrentTime = this.audio?.currentTime || 0;
      };

      this.audio.onended = () => {
        this.setState('pause');
        // this.state.set('pause');
      };
    } else {
      console.warn('No audio recording available to play.');
    }
  }

  pauseAudio(): void {
    if (this.waveSurfer) {
      this.waveSurfer.pause();
      this.setState('pause');
      // this.state.set('pause');
    }

    if (this.audio) {
      this.setState('pause');
      // this.state.set('pause');
      this.audio.pause();
    }
  }

  resumeAudio(): void {
    if (this.audio) {
      this.setState('play');
      // this.state.set('play');
      this.audio.play();
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
